// The seat gate.
//
// The audience arrives by scanning the QR card on their seat, and that card
// carries ?t=<token>. Someone who types the bare address instead arrives with no
// seat at all, and the app has nothing to give them: the socket is refused
// without a token, so the screen stays black and the phone looks broken.
//
// So the bare address answers with the only two things that help: a scanner for
// the card already in their hand, and a way to read about the show.
//
// Camera access needs a secure context, which means this scanner only runs over
// https. On plain http, and whenever the person declines the permission prompt,
// the same panel degrades to the instruction that always works: use the phone's
// own camera app, which reads our QR codes natively.

import jsQR from "jsqr";
import { t, applyLangAttribute, type StringKey } from "./i18n";

const TOKEN_RE = /^[A-Za-z0-9_-]{8}$/;   // 6 bytes base64url, see encodeToken()
// Tonight's code, as it appears on a card. Six characters from an alphabet with
// no 0/O and no 1/I — see server/hub/src/show-code.js, which is where it is
// minted and the only place that decides whether one is any good. This is a
// SHAPE check and nothing more: it decides what to keep out of a scanned URL,
// never whether the door will take it.
const SHOW_RE  = /^[23456789ABCDEFGHJKLMNPQRSTUVWXYZ]{6}$/;

export function hasSeatToken(): boolean {
  const fromUrl = new URLSearchParams(location.search).get("t");
  // sessionStorage so a reload, or a trip to the info page and back, does not
  // send someone who already scanned back to the scanner.
  return !!(fromUrl || sessionStorage.getItem("cs_token"));
}

/**
 * Turn scanned text into a seat URL on THIS deployment, or null.
 *
 * Deliberately strict. A QR code is an instruction from a piece of paper, and in
 * a dark hall nobody is checking what it says: a sticker over ours, or another
 * venue's card, must not be able to send this phone anywhere. Only our own
 * origin, only with a seat token.
 */
export function seatUrlFrom(text: string): string | null {
  const raw = (text ?? "").trim();
  if (!raw) return null;
  if (TOKEN_RE.test(raw)) return `${location.origin}/?t=${raw}`;
  try {
    const u = new URL(raw, location.origin);
    if (u.origin !== location.origin) return null;

    // The show code, kept out of every branch below rather than rebuilt into
    // each one. THIS FUNCTION REBUILDS THE ADDRESS FROM SCRATCH — that is what
    // makes it safe, and it is also why anything it does not name explicitly is
    // silently dropped. Before this line the code on a scanned card was thrown
    // away here, and the phone was then turned back at the door holding a card
    // that was perfectly good.
    // Read under both names, written under the short one. `show` is on every
    // card printed before the parameter was shortened, and this scanner is
    // exactly where such a card arrives.
    const code = (u.searchParams.get("s") ?? u.searchParams.get("show") ?? "").toUpperCase();
    const show = SHOW_RE.test(code) ? `s=${code}` : "";

    const tok = u.searchParams.get("t");
    if (tok) {
      if (!TOKEN_RE.test(tok)) return null;
      return `${location.origin}/?t=${tok}${show ? `&${show}` : ""}`;
    }

    // The projected card: our origin and a code as the whole path. It is the one
    // card printed without a token, and the server answers it with a redirect
    // rather than a page — but this scanner does not have to follow one, so it
    // goes straight to the form the redirect would have produced.
    const seg = u.pathname.replace(/^\/+/, "").replace(/\/+$/, "").toUpperCase();
    if (!u.search && SHOW_RE.test(seg)) return `${location.origin}/?s=${seg}`;
    // Our own address with NOTHING ELSE IN IT: the house card. It used to be
    // refused, and that was right while every card carried a token — a code with
    // no token could only be a mis-scan. The house card carries the origin and
    // nothing else now, so refusing it would leave the one code a hall prints
    // biggest as the one code this scanner cannot read.
    //
    // The path has to be checked, not just the origin, and it is the whole of
    // what keeps this safe: a URL is resolved RELATIVE to our origin, so any
    // scrap of text with no scheme in it — a truncated token, a word, a stray
    // line from a menu — arrives here looking like our host with a path. Bare
    // means bare: root, and nothing in the query but a code we recognise.
    //
    // The `show` allowance is why this is `!u.search || show` rather than the
    // `!u.search` it used to be: a house card printed with a code is our own
    // address with exactly one parameter on it, and refusing that would leave
    // the card a hall prints biggest as the card this scanner cannot read.
    if (u.pathname === "/" && (!u.search || (show && u.searchParams.size === 1))) {
      return `${location.origin}/${show ? `?${show}` : ""}`;
    }
    return null;
  } catch {
    return null;
  }
}

type Hint = "idle" | "starting" | "scanning" | "denied" | "insecure" | "nocam" | "busy" | "wrong";

const HINT_KEYS: Record<Exclude<Hint, "idle">, StringKey> = {
  starting: "gateStarting",
  scanning: "gateScanning",
  denied:   "gateDenied",
  insecure: "gateInsecure",
  nocam:    "gateNoCam",
  busy:     "gateBusy",
  wrong:    "gateWrong",
};

export function mountSeatGate(): void {
  const gate  = document.getElementById("seat-gate");
  const video = document.getElementById("gate-video") as HTMLVideoElement | null;
  const start = document.getElementById("gate-start") as HTMLButtonElement | null;
  const hint  = document.getElementById("gate-hint");
  if (!gate || !video || !start || !hint) return;

  // The app never boots behind this, so its chrome would just be furniture over
  // the scanner.
  document.getElementById("loading-overlay")?.classList.add("hidden");
  document.getElementById("status-bar")?.classList.add("hidden");
  document.getElementById("hud-buttons")?.classList.add("hidden");
  gate.classList.remove("hidden");

  // Every string on this page comes from the phone's own language, including
  // the two that are markup rather than script.
  applyLangAttribute();
  const setText = (sel: string, key: StringKey) => {
    const el = gate.querySelector(sel);
    if (el) el.textContent = t(key);
  };
  setText(".gate-start-main", "gateScan");
  setText(".gate-cta-main", "ctaTitle");
  setText(".gate-cta-sub", "ctaSub");

  const say = (h: Hint) => { hint.textContent = h === "idle" ? "" : t(HINT_KEYS[h]); };

  // getUserMedia is undefined outside a secure context, so this is also the
  // answer to "why does the button do nothing over http".
  const canScan = !!navigator.mediaDevices?.getUserMedia && window.isSecureContext;
  if (!canScan) {
    start.disabled = true;
    start.classList.add("disabled");
    say("insecure");
    return;
  }

  let stream: MediaStream | null = null;
  let stop = false;

  // Chrome on Android decodes in the browser process, which is both faster and
  // easier on the battery than pushing frames through canvas. Everything else,
  // Safari included, goes through jsQR.
  const detector = "BarcodeDetector" in window
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ? new (window as any).BarcodeDetector({ formats: ["qr_code"] })
    : null;

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d", { willReadFrequently: true });

  const finish = (url: string) => {
    stop = true;
    stream?.getTracks().forEach(track => track.stop());
    // replace(), not assign(): the scanner is not somewhere to come back to with
    // the back button once a seat has been claimed.
    location.replace(url);
  };

  async function tick() {
    if (stop) return;
    if (video!.readyState >= 2 && video!.videoWidth > 0) {
      let text: string | null = null;
      try {
        if (detector) {
          const found = await detector.detect(video!);
          text = found?.[0]?.rawValue ?? null;
        } else if (ctx) {
          // Downscale, but not as far as it used to. 480 was chosen on the
          // assumption that the code fills a good part of the frame; a seat card
          // photographed from a chair does not, and at 480 across a whole
          // landscape frame its modules fall below what jsQR can resolve. 720
          // is still cheap enough for an old phone and is what the camera is now
          // asked to produce, so on most phones this is no longer a downscale.
          const w = Math.min(720, video!.videoWidth);
          const h = Math.round(video!.videoHeight * (w / video!.videoWidth));
          canvas.width = w; canvas.height = h;
          ctx.drawImage(video!, 0, 0, w, h);
          const img = ctx.getImageData(0, 0, w, h);
          text = jsQR(img.data, w, h, { inversionAttempts: "dontInvert" })?.data ?? null;
        }
      } catch { /* a frame that fails to decode is not an error, it is a frame */ }

      if (text) {
        const url = seatUrlFrom(text);
        if (url) { finish(url); return; }
        say("wrong");   // read something, but not one of ours
      }
    }
    requestAnimationFrame(() => { void tick(); });
  }

  start.addEventListener("click", async () => {
    // Arming the loop again is the whole reason this line exists, and leaving it
    // out cost an iPhone a working scanner. `stop` latches on the way out —
    // finish() sets it, and so does the visibility handler below — but nothing
    // ever cleared it. So after the page had been hidden even once (switching
    // apps, the screen locking, a notification, the permission sheet: routine on
    // iOS) pressing scan re-opened the camera, showed the preview and the
    // framing box, and tick() returned on its first line, forever. It looked
    // like a camera that could see and a decoder that could not read.
    stop = false;
    say("starting");
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        // The card is on the seat in front of them, so the back camera.
        //
        // The size is asked for, not left to the browser: with no constraint at
        // all iOS hands back its low preset, typically 640x480, and a seat card
        // held at arm's length lands near jsQR's floor. Ideal rather than exact
        // so a camera that cannot do it still opens.
        video: {
          facingMode: { ideal: "environment" },
          width:  { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
    } catch (e) {
      const name = (e as { name?: string })?.name ?? "";
      // NotReadableError is another app or another tab holding the camera, and
      // it used to be reported as a refused permission — which sends somebody
      // into Settings to fix a permission that was never the problem.
      say(name === "NotFoundError" || name === "OverconstrainedError" ? "nocam"
        : name === "NotReadableError" || name === "AbortError" ? "busy"
        : "denied");
      return;
    }
    video.srcObject = stream;
    // playsinline is set in the markup as well: without it iOS takes the video
    // fullscreen and the framing overlay ends up behind the player.
    video.setAttribute("playsinline", "");
    await video.play().catch(() => { /* autoplay policy, the frames still arrive */ });
    gate.classList.add("scanning");
    say("scanning");
    void tick();
  });

  // Stop the camera when the page goes away. A phone that keeps a stream open in
  // the background is a phone with a camera light on in a dark hall.
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      stop = true;
      stream?.getTracks().forEach(track => track.stop());
      gate.classList.remove("scanning");
      say("idle");
      start.disabled = false;
    }
  });
}
